import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { useSearchPerPage } from '../contexts/SearchContext';
import { SRP_HITS_PER_PAGE_OPTIONS, isValidSrpHitsPerPage } from '../utils/srp/srp-hits-per-page';
import { SRP_INLINE_SELECT_CLASSNAME } from '../utils/srp/srp-inline-select-classname';
import { useFormatMessage } from '../i18n-review/client/Msg';

/**
 * Option labels are looked up dynamically below (`formatMessage({ id: option.labelKey, ... })`),
 * so `extract:i18n`'s static extractor can't see them at the call site — this block is the
 * extraction source that keeps every search.resultsPerPage.* id in en.json (mirrors the
 * same pattern in LastSeenSelector).
 */
const resultsPerPageMessages = defineMessages({
  aria: {
    id: 'search.resultsPerPage.aria',
    defaultMessage: 'Results per page',
    description: 'Accessibility label for the results-per-page dropdown on search results',
  },
  optionLabel: {
    id: 'search.resultsPerPage.optionLabel',
    defaultMessage: '{count} per page',
    description: 'Results-per-page option label, e.g. "24 per page"',
  },
});

interface ResultsPerPageSelectorProps {
  className?: string;
}

export const ResultsPerPageSelector: React.FC<ResultsPerPageSelectorProps> = ({ className = '' }) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { perPage, setPerPage } = useSearchPerPage();
  const value = isValidSrpHitsPerPage(perPage) ? perPage : SRP_HITS_PER_PAGE_OPTIONS[0];

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <select
        className={SRP_INLINE_SELECT_CLASSNAME}
        value={value}
        onChange={(e) => setPerPage(Number(e.target.value))}
        aria-label={formatMessage(resultsPerPageMessages.aria)}
      >
        {SRP_HITS_PER_PAGE_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {formatMessage(resultsPerPageMessages.optionLabel, { count: option })}
          </option>
        ))}
      </select>
    </div>
  );
};
